/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import {useContext, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {useSnackbar} from "notistack";
import {FirebaseAuthContext} from "../../../../commons/firebase/FirebaseAuthProvider";
import {useIntl} from "react-intl";
import useCheckVideoDuplicate from "../utils/useCheckVideoDuplicate";
import {AppbarContentContext} from "../../../../commons/routing/appbar/AppbarContentProvider";
import * as tus from "tus-js-client";

const useEditVideo = () => {
    const {signedInUser, isOperationOnObjectAllowed} = useContext(FirebaseAuthContext);
    const history = useHistory();

    const {enqueueSnackbar} = useSnackbar();

    const [saveInProgress, setSaveInProgress] = useState(false)

    // For starting the upload job in background, as this might take long
    const {startBackgroundJob} = useContext(AppbarContentContext)

    // Reference storing the current video upload progress
    const uploadProgress = useRef(0)

    // Internationalization for simple strings
    const intl = useIntl();

    const {handleSaveDuplicate, checkInProgress} = useCheckVideoDuplicate()

    const saveVideoData = async (videoData) => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/videos/' + videoData.key, {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                videoData: {
                    basics: {
                        association: videoData.association,
                        discipline: videoData.discipline,
                        tournament: videoData.tournament,
                        year: videoData.year,
                        ageGroup: videoData.ageGroup,
                        surnameAthlete1: videoData.surnameAthlete1,
                        surnameAthlete2: videoData.surnameAthlete2,
                        gender: videoData.gender,
                        bout: videoData.bout
                    }
                }
            })
        })
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.edit.success',
                        defaultMessage: 'Video successfully saved.'
                    }), {
                        variant: 'success',
                        preventDuplicate: true,
                        autoHideDuration: 3000
                    });

                return data
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.edit.error',
                        defaultMessage: 'Something went wrong while saving the video: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    const saveVideo = async (videoData) => {
        return handleSaveDuplicate(videoData, () => {
            setSaveInProgress(true)
            return saveVideoData(videoData)
                .then(() => {
                    history.push('/admin/videos')
                })
                .finally(() => {
                    setSaveInProgress(false)
                })
        })
    }

    const uploadAndSaveVideo = async (videoData) => {
        return handleVideoCreation(videoData)
    }

    // Upload process according to https://developer.vimeo.com/api/upload/videos#resumable-approach
    const handleVideoCreation = async (videoData) => {
        setSaveInProgress(true)

        // Create video and get upload URL
        return handleGetUploadLink(videoData)
            .then(async (data) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (data.status !== 200) {
                    throw new Error((await data.json()).status)
                }

                // Setting data from the response for later usage
                const json = await data.json();

                let uploadLink = json.uploadLink;
                let accessLink = json.accessLink;
                let playerEmbedUrl = json.playerEmbedUrl;
                let resourceKey = json.resourceKey;
                let uri = json.uri;
                let id = json.id;

                // Add the upload + save job for asynchronous execution (i.e. do not wait here until the video is uploaded)
                return startBackgroundJob(
                    id,
                    `Uploading video ${videoData.surnameAthlete1} vs. ${videoData.surnameAthlete2}`,
                    () => handleUploadAndSaveVideo(videoData, uploadLink, accessLink, playerEmbedUrl, resourceKey, uri, id),
                    uploadProgress
                )
            })
            .catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.uploadLink.error',
                        defaultMessage: 'Something went wrong while creating video upload link: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
            .finally(() => {

                // After the upload + save job is added
                setSaveInProgress(false)
                history.push('/admin/videos');
            })

    }

    const handleGetUploadLink = async (videoData) => {
        const idToken = await signedInUser.getIdToken()

        return fetch('/reg-main/videos/getUploadLink', {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                idToken: idToken,
                fileSize: videoData.videoFile.size,
                metadata: {
                    association: videoData.association,
                    discipline: videoData.discipline,
                    tournament: videoData.tournament,
                    year: videoData.year,
                    ageGroup: videoData.ageGroup,
                    surnameAthlete1: videoData.surnameAthlete1,
                    surnameAthlete2: videoData.surnameAthlete2,
                    gender: videoData.gender,
                    bout: videoData.bout
                }
            })
        })
    }

    const handleUploadAndSaveVideo = async (videoData, uploadLink, accessLink, playerEmbedUrl, resourceKey, uri, id) => {
        const idToken = await signedInUser.getIdToken()

        return uploadVideoViaTus(videoData.videoFile, uploadLink)
            .then(() => {

                // Persist video data after successful upload
                return fetch('/reg-main/videos/' + videoData.key, {
                    method: 'PUT',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({
                        idToken: idToken,
                        videoData: {
                            basics: {
                                association: videoData.association,
                                discipline: videoData.discipline,
                                tournament: videoData.tournament,
                                year: videoData.year,
                                ageGroup: videoData.ageGroup,
                                surnameAthlete1: videoData.surnameAthlete1,
                                surnameAthlete2: videoData.surnameAthlete2,
                                gender: videoData.gender,
                                bout: videoData.bout
                            },
                            vimeo: {
                                accessLink: accessLink,
                                playerEmbedUrl: playerEmbedUrl,
                                resourceKey: resourceKey,
                                uri: uri,
                                id: id
                            }
                        }
                    })
                })
            }).then(async (response) => {
                // Throw an error including the textual status of the HTTP response code (no detailed message necessary)
                if (response.status !== 200) {
                    throw new Error((await response.json()).status)
                }

                // No success message is needed here, because we perform the upload asynchronous for the user
                // Otherwise it would be confusing that after some time (after the upload is finished) the toast pops up
            }).catch((error) => {
                enqueueSnackbar(
                    intl.formatMessage({
                        id: 'app.videos.messages.new.error',
                        defaultMessage: 'Something went wrong while updating the video: {error}'
                    }, {error: error.message}), {
                        variant: 'error',
                        preventDuplicate: true,
                        autoHideDuration: 5000
                    });
            })
    }

    const uploadVideoViaTus = async (videoFile, uploadLink) => {

        // Converting the callback style, so that it can be used with then-chaining style
        return new Promise((resolve, reject) => {
            const upload = new tus.Upload(videoFile, {
                uploadUrl: uploadLink,
                retryDelays: [0, 3000, 5000, 10000, 20000],
                onError: (error) => {
                    reject(error)
                },
                onProgress: (bytesUploaded, bytesTotal) => {
                    const percentage = (bytesUploaded / bytesTotal * 100)
                    uploadProgress.current = percentage
                },
                onSuccess: () => {
                    resolve()
                }
            })
            upload.start()
        })
    }

    const saveVideoAndEditScores = async (videoData, selectedScoredScene, isReadOnlyMode) => {

        /*
          If the user attempts to edit an owned scored scene of a non-editable (i.e. not owned) video, a saving
          and duplication check is not necessary and the user can be instantly redirected to the edit scored scene page.
         */
        if (isReadOnlyMode) {
            history.push('/admin/videos/' + videoData.key + '/scoredScenes/' + selectedScoredScene.key + '/edit')
            return
        }

        return handleSaveDuplicate(videoData, () => {
            setSaveInProgress(true)
            return saveVideoData(videoData)
                .then(async (data) => {
                    const updatedVideo = await data.json()
                    history.push('/admin/videos/' + updatedVideo.key + '/scoredScenes/' + selectedScoredScene.key + '/edit')
                })
                .finally(() => {
                    setSaveInProgress(false)
                })
        })
    }

    const saveVideoAndDefineScores = async (videoData, isReadOnlyMode) => {

        /*
          Although the video is not editable, scored scenes can be created. In the non-editable mode (i.e. read-only),
          a saving and a duplication check is not necessary and the user can instantly be forwarded to the new scored scene screen.
         */
        if (isReadOnlyMode) {
            history.push('/admin/videos/' + videoData.key + '/scoredScenes/new')
            return;
        }

        return handleSaveDuplicate(videoData, () => {
            setSaveInProgress(true)
            return saveVideoData(videoData)
                .then(async (response) => {
                    const updatedVideo = await response.json()
                    history.push('/admin/videos/' + updatedVideo.key + '/scoredScenes/new')
                })
                .finally(() => {
                    setSaveInProgress(false)
                })
        })
    }

    const cancel = () => {
        history.push('/admin/videos');
    }

    return {
        cancel,
        saveVideo,
        uploadAndSaveVideo,
        saveVideoAndDefineScores,
        saveVideoAndEditScores,
        saveInProgress,
        checkInProgress
    }
}
export default useEditVideo;
